import { useDarkMode } from "@/hooks/useDarkMode";
import { useGeoLocation } from "@/hooks/useGeoLocation";
import { useLoadGoogleMaps } from "@/hooks/useLoadGoogleMaps";
import { useSelectedVenue } from "@/hooks/useSelectedVenue";
import {
  leftPanelExpandedAtom,
  mapCenterAtom,
  mobileListViewAtom,
  savedPlaceByUserIdToViewAtom,
  savedPlaceIdToViewAtom,
  savedPlaceSortByToViewAtom,
} from "@/state/atoms";
import { GoogleMap, Marker, OverlayView } from "@react-google-maps/api";
import { defaultRegion, darkModeStyles, lightModeStyles } from "common/config";
import {
  useHomeSearchParams,
  useHomeVenues,
  useI18N,
  useMapSettings,
  useSavedPlaces,
} from "common/hooks";
import { Venue, VenuePinType } from "common/types";
import { useAtom } from "jotai";
import React, { useEffect, useRef, useState } from "react";

import GreyPin from "@/assets/pins/grey_pin.svg";
import OrangePin from "@/assets/pins/orange_pin.svg";
import GreyPinSelected from "@/assets/pins/grey_pin_selected.svg";
import OrangePinSelected from "@/assets/pins/orange_pin_selected.svg";

import YouAre from "@/assets/pins/youAre.svg";
import AnalyticsService from "@/services/analytics";
import VenueCard from "@/components/template/VenueCard";
import { ChevronLeftIcon, ChevronRightIcon } from "lucide-react";
import { FiMapPin } from "react-icons/fi";
import { cn } from "@/lib/utils";
import { TbCurrentLocation } from "react-icons/tb";
import { FaMinus, FaPlus } from "react-icons/fa6";
import Loader from "@/components/common/Loader";
import ModalWithAction from "@/components/common/ModalWithAction";

function MapView(props: { query: string; hidden?: boolean }) {
  const [savedPlaceIdToView] = useAtom(savedPlaceIdToViewAtom);
  const [savedPlaceByUserIdToView] = useAtom(savedPlaceByUserIdToViewAtom);
  const [savedPlaceSortByToView] = useAtom(savedPlaceSortByToViewAtom);

  const i18n = useI18N();
  const [mapRef, setMapRef] = useState<google.maps.Map | null>(null);
  const [isLeftPaneVisible, setIsLeftPaneVisible] = useAtom(
    leftPanelExpandedAtom
  );

  //const [, setIsFilterSheetOpen] = useAtom(filtersOpenAtom);
  const { mapSettings } = useMapSettings();

  const [activeMarker, setActiveMarker] = useState<string | null>(null);

  const { selectedVenue, setSelectedVenue } = useSelectedVenue();

  const [, setMobileListView] = useAtom(mobileListViewAtom);

  const { isMapsLoaded } = useLoadGoogleMaps();

  const [mapCenter, setMapCenter] = useAtom(mapCenterAtom);
  const isDark = useDarkMode();

  const { data } = useSavedPlaces();
  const savedPlaces = data?.saved_places || [];

  const markerRefs = useRef(new Map());

  const [isLocationModalOpen, setIsLocationModalOpen] = useState(false);

  const handleSetLocation = async () => {
    //set user location
    // console.log("inside handleSetLocation");
    setIsLocationModalOpen(false);
    return;
  };

  const handleActiveMarker = (
    marker: string,
    newPosition: google.maps.LatLng,
    location: Venue
  ) => {
    if (marker === activeMarker) {
      return;
    }
    setActiveMarker(null);
    setTimeout(() => {
      const overlayHeight = 400; // Adjust according to your overlay's height
      // Calculate the pixel offset to move the map
      const scale = Math.pow(2, mapRef.getZoom());
      const worldCoordinateCenter = mapRef
        .getProjection()
        .fromLatLngToPoint(newPosition);
      const pixelOffset = new google.maps.Point(
        0, // No horizontal shift
        overlayHeight / 2 / scale // Vertical shift
      );

      const newWorldCoordinate = new google.maps.Point(
        worldCoordinateCenter.x - pixelOffset.x,
        worldCoordinateCenter.y - pixelOffset.y
      );

      const newCenter = mapRef
        .getProjection()
        .fromPointToLatLng(newWorldCoordinate);

      mapRef.panTo(newCenter);
      setMobileListView(true);
      setIsLeftPaneVisible(true);
      setActiveMarker(marker);
      setSelectedVenue(location);
    }, 100);
  };

  const {
    location: userLocation,
    loading: locationLoading,
    error: locationError,
    requestUserLocation,
  } = useGeoLocation();

  //result of search api
  const { locations, isLoading } = useHomeVenues();

  const { setHomeSearchParams, homeSearchParams } = useHomeSearchParams();

  useEffect(() => {
    if (userLocation && mapRef) {
      if (savedPlaceIdToView == "") {
        mapRef.panTo({
          lat: userLocation.latitude,
          lng: userLocation.longitude,
        });
      }
      setHomeSearchParams((prev) => ({
        ...prev,
        userlatitude: userLocation.latitude.toString(),
        userlongitude: userLocation.longitude.toString(),
        latitude: userLocation.latitude.toString(),
        longitude: userLocation.longitude.toString(),
        zoom: mapRef?.getZoom().toString() || "15",
        saved_place_id: savedPlaceIdToView,
        saved_place_user_id: savedPlaceByUserIdToView,
        saved_place_sortby: savedPlaceSortByToView,
        unixTimestamp: Math.floor(Date.now() / 1000),
      }));
    }
  }, [
    userLocation,
    mapRef,
    setHomeSearchParams,
    setMapCenter,
    savedPlaceIdToView,
    savedPlaceByUserIdToView,
    savedPlaceSortByToView,
  ]);

  useEffect(() => {
    if (locationError) {
      if (mapRef) {
        console.log(
          "Setting default location and loading pins savedPlaceIdToView",
          savedPlaceIdToView,
          "savedPlaceByUserIdToView",
          savedPlaceByUserIdToView
        );
        if (savedPlaceIdToView == "") {
          mapRef.panTo({
            lat: defaultRegion.latitude,
            lng: defaultRegion.longitude,
          });
        }
        setHomeSearchParams((prev) => ({
          ...prev,
          userlatitude: defaultRegion.latitude.toString(),
          userlongitude: defaultRegion.longitude.toString(),
          latitude: defaultRegion.latitude.toString(),
          longitude: defaultRegion.longitude.toString(),
          zoom: mapRef?.getZoom().toString() || "15",
          saved_place_id: savedPlaceIdToView,
          saved_place_user_id: savedPlaceByUserIdToView,
          saved_place_sortby: savedPlaceSortByToView,
          unixTimestamp: Math.floor(Date.now() / 1000),
        }));
      }
    }
  }, [
    locationError,
    mapRef,
    savedPlaceByUserIdToView,
    savedPlaceIdToView,
    savedPlaceSortByToView,
    setHomeSearchParams,
    setMapCenter,
  ]);

  const onMapMoved = () => {
    if (savedPlaceIdToView !== "") {
      console.log("No progresive search on View saved places");
      return;
    }

    if (activeMarker) {
      console.log("Marker active not loading new pins");
      return;
    }

    //check how much the map moved
    //if the map moved more than 1km, then fetch the venues
    //if the map moved less than 1km, then don't fetch the venues
    const prevCenter = new google.maps.LatLng(
      parseFloat(homeSearchParams.latitude),
      parseFloat(homeSearchParams.longitude)
    );
    const newCenter = mapRef?.getCenter();

    const distanceInMeters =
      google.maps.geometry.spherical.computeDistanceBetween(
        prevCenter,
        newCenter
      );
    //console.log("Distance changed: ", distanceInMeters);
    if (
      distanceInMeters >
      parseFloat(mapSettings?.mapUpdateDistance || "0.5") * 1000
    ) {
      console.log(
        "onMapMoved Reloading pins",
        newCenter?.lat().toString(),
        newCenter?.lng().toString()
      );
      setHomeSearchParams((prev) => ({
        ...prev,
        latitude: newCenter?.lat().toString() || "",
        longitude: newCenter?.lng().toString() || "",
        zoom: mapRef?.getZoom().toString() || "15",
        searchThisAreaClicked: "no",
        unixTimestamp: Math.floor(Date.now() / 1000),
      }));
    } else {
      console.log("Not reloading pins");
    }
  };

  const onLoad = React.useCallback(function callback(map: google.maps.Map) {
    setMapRef(map);
  }, []);

  const onUnmount = React.useCallback(function callback() {
    setMapRef(null);
  }, []);

  const getPinIcon = (venue: Venue) => {
    if (venue.venuePinType === VenuePinType.Grey) {
      return GreyPin;
    } else {
      return OrangePin;
    }
  };

  const getSelectedPinIcon = (venue: Venue) => {
    if (venue.venuePinType === VenuePinType.Grey) {
      return GreyPinSelected;
    } else {
      return OrangePinSelected;
    }
  };

  const radius = 75; // default radius in km
  const unit = "km"; // or 'miles'
  const toRadians = (degrees) => {
    return degrees * (Math.PI / 180);
  };

  useEffect(() => {
    // Debugging output to ensure locations are received
    console.log("locations", locations);

    // Initialize pin counters
    let orangePin = 0;
    let greyPin = 0;

    // Iterate over the locations and count pins
    locations.map((location) => {
      if (location.reviewsDetailsSubmitted > 0) {
        orangePin++; // Count orange pin
      } else {
        greyPin++; // Count grey pin
      }
    });

    // Handling search term for Mixpanel tracking
    let searchTermForMixPanel = homeSearchParams.search;
    if (searchTermForMixPanel === "undefined") {
      searchTermForMixPanel = ""; // Fallback if the search term is undefined
    }

    // Tracking the search event in Google Analytics and Mixpanel
    // console.log("homeSearchParams: ", homeSearchParams);
    AnalyticsService.trackGAEventHandler("homeSearchPerformed");
    AnalyticsService.trackMx("homeSearchPerformed", {
      SearchTerm: searchTermForMixPanel,
      totalResults: locations.length,
      orangePin: orangePin,
      greyPin: greyPin,
    });
    // console.log("inside homeSearchPerformed");

  }, [homeSearchParams]); // Added dependencies for useEffect

  useEffect(() => {
    if (
      locations.length &&
      mapRef &&
      userLocation &&
      savedPlaceIdToView !== ""
    ) {
      const calculateDistance = (lat1, lon1, lat2, lon2, unit) => {
        const R = unit === "miles" ? 3958.8 : 6371; // Radius of the Earth in miles or km
        const dLat = toRadians(lat2 - lat1);
        const dLon = toRadians(lon2 - lon1);
        const a =
          Math.sin(dLat / 2) * Math.sin(dLat / 2) +
          Math.cos(toRadians(lat1)) *
            Math.cos(toRadians(lat2)) *
            Math.sin(dLon / 2) *
            Math.sin(dLon / 2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        const distance = R * c;
        return distance;
      };

      const filterLocationsByRadius = (locations, center, radius, unit) => {
        return locations.filter((location) => {
          const distance = calculateDistance(
            center.latitude,
            center.longitude,
            location.latitude,
            location.longitude,
            unit
          );
          return distance <= radius;
        });
      };

      const filteredLocations = filterLocationsByRadius(
        locations,
        userLocation,
        radius,
        unit
      );
      const bounds = new google.maps.LatLngBounds();
      filteredLocations.forEach((location) => {
        bounds.extend(
          new google.maps.LatLng(location.latitude, location.longitude)
        );
      });

      bounds.extend(
        new google.maps.LatLng(userLocation.latitude, userLocation.longitude)
      );

      mapRef.fitBounds(bounds);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locations, userLocation, radius, unit, mapRef]);

  // code to show venue marker highlighted
  useEffect(() => {
    if (selectedVenue && savedPlaceIdToView == "" && mapRef) {
      const newPosition = new google.maps.LatLng({
        lat: selectedVenue.latitude,
        lng: selectedVenue.longitude,
      });
      mapRef.panTo(newPosition);
      setActiveMarker(selectedVenue.id);
    } else {
      setActiveMarker(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVenue, mapRef]);

  // mapId: googleMapId 
  const mapOptions = {
    keyboardShortcuts: false,
    fullscreenControl: false,
    disableDefaultUI: true,
    ...(isDark
      ? { styles: darkModeStyles } // Add dark mode styles if isDark is true
      : { styles: lightModeStyles }), // Add mapId if isDark is false
  };

  return (
    <div
      className={cn(
        "flex-1 relative h-full rounded-[12px] overflow-clip",
        props.hidden ? "hidden" : "block"
      )}
    >
      {isMapsLoaded && (
        <GoogleMap
          mapContainerStyle={{
            width: "100%",
            height: "100%",
          }}
          center={mapCenter}
          zoom={16}
          onLoad={onLoad}
          onUnmount={onUnmount}
          options={mapOptions} // Pass the dynamic mapOptions here
          //onDragEnd={() => onMapMoved()}
          onClick={() => setActiveMarker(null)}
          onIdle={() => onMapMoved()}
        >
          {locations.map((location, index) => (
            <Marker
              ref={(marker) => {
                if (marker) {
                  markerRefs.current.set(location.id, marker);
                }
              }}
              key={location.id}
              position={{
                lat: location.latitude,
                lng: location.longitude,
              }}
              title={location.name}
              zIndex={
                selectedVenue && selectedVenue.id === location.id ? 1000 : index
              }
              icon={{
                url:
                  selectedVenue && selectedVenue.id === location.id
                    ? getSelectedPinIcon(location)
                    : getPinIcon(location),
                anchor: new google.maps.Point(15, 30),
                size:
                  selectedVenue && selectedVenue.id === location.id
                    ? new google.maps.Size(40, 40)
                    : new google.maps.Size(30, 30),
                scaledSize:
                  selectedVenue && selectedVenue.id === location.id
                    ? new google.maps.Size(40, 40)
                    : new google.maps.Size(30, 30),
              }}
              onClick={() => {
                AnalyticsService.trackGAEventHandler(
                  "homePickedVenueFromSearchResultsMap"
                );
                AnalyticsService.trackMx(
                  "homePickedVenueFromSearchResultsMap",
                  {
                    VenueId: location.id,
                    VenueBrandId: location.brandId,
                    VenueBrandName: location.brandName,
                    VenueAddress: location.address,
                    VenueName: location.name,
                    VenueType: location.businessType,
                  }
                );
                const newPosition = new google.maps.LatLng({
                  lat: location.latitude,
                  lng: location.longitude,
                });
                handleActiveMarker(location.id, newPosition, location);
              }}
            >
              {activeMarker === location.id ? (
                <OverlayView
                  mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                  position={
                    (
                      markerRefs.current?.get(activeMarker) as Marker
                    ).marker.getPosition() as google.maps.LatLng
                  }
                  getPixelPositionOffset={(width, height) => ({
                    x: -(width / 2),
                    y: -(height / 2),
                  })}
                >
                  <div
                    onClick={() => {
                      setSelectedVenue(location);
                    }}
                    className="bg-white dark:bg-primary-900 rounded-lg shadow-md min-w-[300px] translate-x-[-50%] translate-y-[-105%]"
                  >
                    <VenueCard
                      location={location}
                      onOpen={() => {}}
                      onSaveVenue={() => {}}
                      savedPlaces={savedPlaces}
                    />
                  </div>
                </OverlayView>
              ) : null}
            </Marker>
          ))}
          {userLocation && (
            <Marker
              key={"userLocation"}
              position={{
                lat: userLocation.latitude,
                lng: userLocation.longitude,
              }}
              title={"Your Location"}
              icon={{
                url: YouAre,
                anchor: new google.maps.Point(15, 30),
                size: new google.maps.Size(30, 30),
                scaledSize: new google.maps.Size(30, 30),
              }}
            ></Marker>
          )}
        </GoogleMap>
      )}
      {/* Lefbar toggle button */}
      <div
        onClick={() => {
          setIsLeftPaneVisible(!isLeftPaneVisible);
        }}
        style={{
          boxShadow: "0px 4px 4px rgba(0, 0, 0, 0.25)",
        }}
        className="hidden md:block absolute left-2 top-2 rounded-[12px] bg-primary-50 dark:bg-dark-800 p-2 z-10 cursor-pointer"
      >
        {isLeftPaneVisible ? (
          <ChevronLeftIcon className="w-6 h-6 text-primary-900 dark:text-primary-50" />
        ) : (
          <ChevronRightIcon className="w-6 h-6 text-primary-900 dark:text-primary-50" />
        )}
      </div>
      {/* Show nearby */}
      {savedPlaceIdToView === "" && (
        <div className="absolute left-0 right-0 top-2 flex items-center justify-center">
          <button
            style={{
              boxShadow: "0px 4px 4px rgba(0, 0, 0, 0.25)",
            }}
            onClick={() => {
              AnalyticsService.trackGAEventHandler(
                "homeSwitchedToSearchThisArea"
              );
              AnalyticsService.trackMx("homeSwitchedToSearchThisArea", {});

              setHomeSearchParams((prev) => ({
                ...prev,
                latitude: mapRef.getCenter().lat().toString(),
                longitude: mapRef.getCenter().lng().toString(),
                userlatitude: userLocation?.latitude.toString() || "",
                userlongitude: userLocation?.longitude.toString() || "",
                searchThisAreaClicked: "yes",
                zoom: mapRef?.getZoom().toString() || "15",
                saved_place_id: savedPlaceIdToView,
                saved_place_user_id: savedPlaceByUserIdToView,
                saved_place_sortby: savedPlaceSortByToView,
                unixTimestamp: Math.floor(Date.now() / 1000),
              }));
            }}
            className={cn(
              "rounded-[12px] px-[16px] py-[8px] bg-primary-50 dark:bg-dark-800 paragraph-small-semibold text-primary-800 dark:text-primary-100 flex items-center"
            )}
          >
            <span className="w-6 h-6 relative mr-1">
              <FiMapPin size={22} />
            </span>
            {i18n.get("showNearBy")}
          </button>
        </div>
      )}
      {/* Goto current location, +, - */}
      <div className="absolute right-0 bottom-0 top-0 flex flex-col items-end justify-end">
        <button
          style={{
            boxShadow: "0px 4px 4px rgba(0, 0, 0, 0.25)",
          }}
          onClick={() => {
            AnalyticsService.trackGAEventHandler("homeZoomedInMapView");
            AnalyticsService.trackMx("homeZoomedInMapView", {});
            mapRef.getZoom() < 17 && mapRef.setZoom(mapRef.getZoom() + 1);
          }}
          className="p-2 mb-1 mr-2 flex items-center justify-center bg-primary-50 dark:bg-primary-800 rounded-[12px] font-semibold"
        >
          <FaPlus size={22} color={isDark ? "white" : "#121042"} />
        </button>
        <button
          style={{
            boxShadow: "0px 4px 4px rgba(0, 0, 0, 0.25)",
          }}
          onClick={() => {
            AnalyticsService.trackGAEventHandler("homeZoomOutMapView");
            AnalyticsService.trackMx("homeZoomOutMapView", {});
            mapRef.getZoom() > 0 && mapRef.setZoom(mapRef.getZoom() - 1);
          }}
          className="p-2 mb-8 mr-2 flex items-center justify-center bg-primary-50 dark:bg-primary-800 rounded-[12px] font-semibold"
        >
          <FaMinus size={22} color={isDark ? "white" : "#121042"} />
        </button>
        <button
          style={{
            boxShadow: "0px 4px 4px rgba(0, 0, 0, 0.25)",
          }}
          onClick={() => {
            console.log("Requesting location");
            AnalyticsService.trackGAEventHandler("homeGeolocatedOnMap");
            AnalyticsService.trackMx("homeGeolocatedOnMap", {});

            // Conditional logic based on locationError
            if (locationError) {
              setIsLocationModalOpen(true);
            } else {
              requestUserLocation();
            }
          }}
          className="p-2 mb-4 mr-2 flex items-center justify-center bg-primary-50 dark:bg-primary-800 rounded-[12px] font-semibold"
        >
          <TbCurrentLocation size={22} color={isDark ? "white" : "#121042"} />
        </button>
      </div>
      {(isLoading || locationLoading) && <Loader />}

      <ModalWithAction
        isModalOpen={isLocationModalOpen}
        title={i18n.get("loctionSettingsRequired")}
        subtitle={i18n.get("loctionSettingsRequiredMessage")}
        cancelButtonTitle={""}
        actionButtonTitle={i18n.get("ok")}
        onClose={() => {
          setIsLocationModalOpen(false);
        }}
        onClick={handleSetLocation}
        type="default"
        actionButtonType="default"
      />
    </div>
  );
}

export default MapView;
